<template>
  <NavBar v-if="userLoaded" pageName="RolesSettings"></NavBar>

  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- Header with actions -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <h2>{{ t.users?.roles || 'Roles Management' }}</h2>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadRoles">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          {{ t.common?.reset || 'Refresh' }}
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon>
          {{ t.common?.add || 'Add Role' }}
        </v-btn>
        <v-btn color="info" @click="openAssignDialog">
          <v-icon class="mr-2">mdi-account-plus</v-icon>
          Assign Role
        </v-btn>
      </v-col>
    </v-row>

    <!-- Roles Table -->
    <v-card>
      <v-card-title>{{ t.users?.roles || 'Roles' }}: {{ roles.length }}</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="roles"
          :loading="loading"
          :items-per-page="20"
          item-value="id"
          class="elevation-1"
        >
          <!-- Current user has role indicator -->
          <template #[`item.current_user_has_role`]="{ item }">
            <v-icon v-if="item.current_user_has_role" color="success" size="small">mdi-circle</v-icon>
            <v-icon v-else color="error" size="small">mdi-circle-outline</v-icon>
          </template>

          <!-- System role indicator -->
          <template #[`item.is_protected`]="{ item }">
            <v-icon v-if="item.is_protected" color="warning">mdi-lock</v-icon>
            <v-icon v-else color="grey">mdi-lock-open</v-icon>
          </template>

          <!-- Available to subclients -->
          <template #[`item.available_to_subclients`]="{ item }">
            <v-icon v-if="item.available_to_subclients" color="success">mdi-check-circle</v-icon>
            <v-icon v-else color="grey">mdi-close-circle</v-icon>
          </template>

          <!-- Available only to distributors -->
          <template #[`item.available_only_to_distributors`]="{ item }">
            <v-icon v-if="item.available_only_to_distributors" color="info">mdi-check-circle</v-icon>
            <v-icon v-else color="grey">mdi-close-circle</v-icon>
          </template>

          <!-- Managed by client -->
          <template #[`item.managed_by_client_name`]="{ item }">
            <span>{{ item.managed_by_client_name || '-' }}</span>
          </template>

          <!-- Actions column -->
          <template #[`item.actions`]="{ item }">
            <v-btn
              size="x-small"
              color="info"
              class="mr-1"
              @click="openViewUsersDialog(item)"
              title="View users with this role"
            >
              <v-icon>mdi-account-group</v-icon>
            </v-btn>
            <v-btn
              size="x-small"
              color="purple"
              class="mr-1"
              @click="openPermissionsDialog(item)"
              :disabled="!item.is_editable"
              title="Edit permissions"
            >
              <v-icon>mdi-key</v-icon>
            </v-btn>
            <v-btn
              size="x-small"
              color="primary"
              class="mr-1"
              @click="openEditDialog(item)"
              :disabled="!item.is_editable"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              size="x-small"
              color="error"
              @click="openDeleteDialog(item)"
              :disabled="item.is_protected || !item.is_editable"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <v-alert type="info" class="ma-2">
              {{ t.messages?.no_results || 'No roles found' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Legend info -->
    <v-alert type="info" density="compact" class="mt-4">
      <v-icon class="mr-2">mdi-information</v-icon>
      <strong>Legend:</strong>
      <v-icon size="small" color="success" class="ml-3">mdi-circle</v-icon> You have this role
      <v-icon size="small" color="error" class="ml-3">mdi-circle-outline</v-icon> You don't have this role
      <v-icon size="small" class="ml-3">mdi-lock</v-icon> Protected
      <v-icon size="small" color="success" class="ml-3">mdi-check-circle</v-icon> Subclients
      <v-icon size="small" color="info" class="ml-3">mdi-check-circle</v-icon> Distributors only
    </v-alert>

    <!-- Add/Edit Role Dialog -->
    <v-dialog v-model="roleDialog" max-width="600">
      <v-card>
        <v-card-title :class="isEditing ? 'bg-primary text-white' : 'bg-success text-white'">
          <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ isEditing ? (t.common?.edit || 'Edit Role') : (t.common?.add || 'Add Role') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="roleForm">
            <v-text-field
              v-model="roleData.name"
              :label="t.common?.name || 'Name'"
              :rules="[v => !!v || 'Name is required', v => !v || v.length >= 2 || 'Min 2 characters']"
              dense
              outlined
              class="mb-3"
            ></v-text-field>

            <v-text-field
              v-model="roleData.description"
              label="Description"
              hint="A brief description of what this role does"
              persistent-hint
              dense
              outlined
              class="mb-3"
            ></v-text-field>

            <v-divider class="my-4"></v-divider>

            <v-switch
              v-model="roleData.is_protected"
              label="Protected Role"
              color="warning"
              hint="Protected roles cannot be deleted"
              persistent-hint
              density="compact"
              class="mb-3"
            >
              <template v-slot:label>
                <span class="d-flex align-center">
                  <v-icon class="mr-2" :color="roleData.is_protected ? 'warning' : 'grey'">
                    {{ roleData.is_protected ? 'mdi-lock' : 'mdi-lock-open' }}
                  </v-icon>
                  Protected Role
                </span>
              </template>
            </v-switch>

            <v-switch
              v-model="roleData.available_to_subclients"
              label="Available to Subclients"
              color="success"
              hint="If enabled, subclients can assign this role to their users"
              persistent-hint
              density="compact"
              class="mb-3"
            >
              <template v-slot:label>
                <span class="d-flex align-center">
                  <v-icon class="mr-2" :color="roleData.available_to_subclients ? 'success' : 'grey'">
                    {{ roleData.available_to_subclients ? 'mdi-account-multiple-check' : 'mdi-account-multiple' }}
                  </v-icon>
                  Available to Subclients
                </span>
              </template>
            </v-switch>

            <v-switch
              v-model="roleData.available_only_to_distributors"
              label="Distributors Only"
              color="info"
              hint="If enabled, only distributor clients can use this role"
              persistent-hint
              density="compact"
            >
              <template v-slot:label>
                <span class="d-flex align-center">
                  <v-icon class="mr-2" :color="roleData.available_only_to_distributors ? 'info' : 'grey'">
                    {{ roleData.available_only_to_distributors ? 'mdi-store-check' : 'mdi-store' }}
                  </v-icon>
                  Distributors Only
                </span>
              </template>
            </v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="roleDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn
            :color="isEditing ? 'primary' : 'success'"
            @click="saveRole"
            :loading="saving"
          >
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="bg-error text-white">
          <v-icon class="mr-2">mdi-delete</v-icon>
          {{ t.common?.delete || 'Delete Role' }}
        </v-card-title>
        <v-card-text class="pt-4">
          <p>{{ t.messages?.confirm_delete || 'Are you sure you want to delete this role?' }}</p>
          <div class="mt-3 pa-3 bg-grey-lighten-4 rounded">
            <div><strong>{{ t.common?.name || 'Name' }}:</strong> {{ selectedRole?.name }}</div>
            <div><strong>Description:</strong> {{ selectedRole?.description || '-' }}</div>
          </div>
          <v-alert type="warning" density="compact" class="mt-3">
            Users with this role will lose it after deletion.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn color="error" @click="deleteRole" :loading="deleting">
            {{ t.common?.delete || 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Role Dialog -->
    <v-dialog v-model="assignDialog" max-width="600">
      <v-card>
        <v-card-title class="bg-info text-white">
          <v-icon class="mr-2">mdi-account-plus</v-icon>
          Assign Role to User
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="assignData.role_id"
                :items="roles"
                item-title="name"
                item-value="id"
                label="Select Role"
                :rules="[v => !!v || 'Role is required']"
                dense
                outlined
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="assignData.client_id"
                :items="clientsList"
                item-title="name"
                item-value="id"
                label="Select Client"
                :rules="[v => !!v || 'Client is required']"
                dense
                outlined
                @update:model-value="loadUsersForClient"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="assignData.user_id"
                :items="usersList"
                :item-title="getUserDisplayName"
                item-value="id"
                label="Select User"
                :rules="[v => !!v || 'User is required']"
                :loading="loadingUsers"
                :disabled="!assignData.client_id"
                dense
                outlined
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props">
                    <v-list-item-subtitle>{{ item.raw.email }}</v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="assignDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn
            color="info"
            @click="assignRole"
            :loading="assigning"
            :disabled="!assignData.role_id || !assignData.user_id"
          >
            Assign Role
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Users with Role Dialog -->
    <v-dialog v-model="viewUsersDialog" max-width="700">
      <v-card>
        <v-card-title class="bg-info text-white">
          <v-icon class="mr-2">mdi-account-group</v-icon>
          Users with role: {{ viewUsersRole?.name }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-progress-linear v-if="viewUsersLoading" indeterminate color="info"></v-progress-linear>

          <div v-else-if="usersWithRole.length === 0">
            <v-alert type="info">No users found with this role.</v-alert>
          </div>

          <div v-else>
            <v-expansion-panels variant="accordion">
              <v-expansion-panel
                v-for="group in usersWithRole"
                :key="group.client_name"
              >
                <v-expansion-panel-title>
                  <v-icon class="mr-2">mdi-domain</v-icon>
                  {{ group.client_name }}
                  <v-chip size="small" class="ml-2">{{ group.users.length }}</v-chip>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <v-list density="compact">
                    <v-list-item
                      v-for="user in group.users"
                      :key="user.id"
                    >
                      <template v-slot:prepend>
                        <v-icon>mdi-account</v-icon>
                      </template>
                      <v-list-item-title>{{ user.username || user.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="info" variant="outlined" @click="assignRoleFromViewUsers">
            <v-icon class="mr-2">mdi-account-plus</v-icon>
            Assign Role
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="viewUsersDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permissions Dialog -->
    <v-dialog v-model="permissionsDialog" max-width="900" scrollable>
      <RolePermissionsEditor
        v-if="permissionsDialog && selectedRoleForPermissions"
        :role-id="selectedRoleForPermissions.id"
        :role-name="selectedRoleForPermissions.name"
        :token="token"
        @close="permissionsDialog = false"
        @saved="onPermissionsSaved"
      />
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import RolePermissionsEditor from "@/components/RolePermissionsEditor.vue";
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { getRolesList, addRole, updateRole, deleteRole, toggleUserRole, getUsersByRole } from "@/composables/useGetRolesAPI";
import { getClientTree, getClientUsers } from "@/composables/useGetClientsAPI";
import { log } from "@/composables/useLog";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'RolesSettings',
  components: { NavBar, RolePermissionsEditor },
  data() {
    return {
      loading: false,
      token: '',
      userLoaded: false,
      roles: [],
      currentClientId: null,

      // Role dialog
      roleDialog: false,
      isEditing: false,
      saving: false,
      roleData: {
        id: null,
        name: '',
        description: '',
        is_protected: false,
        available_to_subclients: false,
        available_only_to_distributors: false
      },

      // Delete dialog
      deleteDialog: false,
      deleting: false,
      selectedRole: null,

      // Assign role dialog
      assignDialog: false,
      assigning: false,
      clientsList: [],
      usersList: [],
      loadingUsers: false,
      assignData: {
        role_id: null,
        client_id: null,
        user_id: null
      },

      // View users with role dialog
      viewUsersDialog: false,
      viewUsersLoading: false,
      viewUsersRole: null,
      usersWithRole: [],

      // Permissions dialog
      permissionsDialog: false,
      selectedRoleForPermissions: null,

      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    tableHeaders() {
      return [
        { title: '', key: 'current_user_has_role', align: 'center', width: '40px', sortable: false },
        { title: 'ID', key: 'id', width: '60px' },
        { title: this.t.common?.name || 'Name', key: 'name', width: '150px' },
        { title: 'Description', key: 'description', width: '200px' },
        { title: 'Managed By', key: 'managed_by_client_name', width: '120px' },
        { title: 'Protected', key: 'is_protected', align: 'center', width: '80px' },
        { title: 'Subclients', key: 'available_to_subclients', align: 'center', width: '90px' },
        { title: 'Distrib.', key: 'available_only_to_distributors', align: 'center', width: '80px' },
        { title: this.t.common?.actions || 'Actions', key: 'actions', sortable: false, width: '130px' }
      ];
    }
  },
  methods: {
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    async loadRoles() {
      this.loading = true;
      try {
        const result = await getRolesList(this.token);
        log("Roles loaded:", result);
        if (!result.error) {
          this.roles = result.roles || [];
          this.currentClientId = result.current_client_id;
        } else {
          this.showSnackbar(result.message || 'Error loading roles', 'error');
        }
      } catch (err) {
        log("Error loading roles:", err);
        this.showSnackbar('Error loading roles', 'error');
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.isEditing = false;
      this.roleData = {
        id: null,
        name: '',
        description: '',
        is_protected: false,
        available_to_subclients: false,
        available_only_to_distributors: false
      };
      this.roleDialog = true;
    },
    openEditDialog(role) {
      this.isEditing = true;
      this.roleData = {
        id: role.id,
        name: role.name,
        description: role.description || '',
        is_protected: role.is_protected || false,
        available_to_subclients: role.available_to_subclients || false,
        available_only_to_distributors: role.available_only_to_distributors || false
      };
      this.roleDialog = true;
    },
    async saveRole() {
      if (!this.roleData.name || this.roleData.name.length < 2) {
        this.showSnackbar('Please enter a valid role name', 'error');
        return;
      }

      this.saving = true;
      try {
        let result;
        if (this.isEditing) {
          result = await updateRole(this.token, this.roleData);
        } else {
          result = await addRole(this.token, this.roleData);
        }

        if (!result.error) {
          this.showSnackbar(this.isEditing ? 'Role updated successfully' : 'Role added successfully');
          this.roleDialog = false;
          await this.loadRoles();
        } else {
          this.showSnackbar(result.message || 'Error saving role', 'error');
        }
      } catch (err) {
        log("Error saving role:", err);
        this.showSnackbar('Error saving role', 'error');
      } finally {
        this.saving = false;
      }
    },
    openDeleteDialog(role) {
      if (role.is_protected) {
        this.showSnackbar('Protected roles cannot be deleted', 'warning');
        return;
      }
      this.selectedRole = role;
      this.deleteDialog = true;
    },
    async deleteRole() {
      if (!this.selectedRole) return;

      this.deleting = true;
      try {
        const result = await deleteRole(this.token, this.selectedRole.id);

        if (!result.error) {
          this.showSnackbar('Role deleted successfully');
          this.deleteDialog = false;
          await this.loadRoles();
        } else {
          this.showSnackbar(result.message || 'Error deleting role', 'error');
        }
      } catch (err) {
        log("Error deleting role:", err);
        this.showSnackbar('Error deleting role', 'error');
      } finally {
        this.deleting = false;
      }
    },

    // Assign Role methods
    async openAssignDialog() {
      this.assignData = {
        role_id: null,
        client_id: null,
        user_id: null
      };
      this.usersList = [];
      this.assignDialog = true;

      // Load clients list
      try {
        const result = await getClientTree(this.token);
        log("Clients loaded:", result);
        if (!result.error) {
          this.clientsList = result.clients_list || [];
        } else {
          this.showSnackbar(result.message || 'Error loading clients', 'error');
        }
      } catch (err) {
        log("Error loading clients:", err);
        this.showSnackbar('Error loading clients', 'error');
      }
    },

    async loadUsersForClient() {
      if (!this.assignData.client_id) {
        this.usersList = [];
        return;
      }

      this.loadingUsers = true;
      this.assignData.user_id = null;
      try {
        const result = await getClientUsers(this.token, this.assignData.client_id, {
          include_subclients: false,
          subclient_id: this.assignData.client_id
        });
        log("Users loaded:", result);
        if (!result.error) {
          this.usersList = result.users || [];
        } else {
          this.showSnackbar(result.message || 'Error loading users', 'error');
        }
      } catch (err) {
        log("Error loading users:", err);
        this.showSnackbar('Error loading users', 'error');
      } finally {
        this.loadingUsers = false;
      }
    },

    getUserDisplayName(user) {
      return `${user.username || user.name || ''} (${user.email || ''})`;
    },

    async assignRole() {
      if (!this.assignData.role_id || !this.assignData.user_id) {
        this.showSnackbar('Please select both a role and a user', 'warning');
        return;
      }

      this.assigning = true;
      try {
        const result = await toggleUserRole(this.token, this.assignData.user_id, this.assignData.role_id);

        if (!result.error) {
          this.showSnackbar('Role assigned successfully');
          this.assignDialog = false;
        } else {
          this.showSnackbar(result.message || 'Error assigning role', 'error');
        }
      } catch (err) {
        log("Error assigning role:", err);
        this.showSnackbar('Error assigning role', 'error');
      } finally {
        this.assigning = false;
      }
    },

    // View Users with Role methods
    async openViewUsersDialog(role) {
      this.viewUsersRole = role;
      this.usersWithRole = [];
      this.viewUsersDialog = true;
      this.viewUsersLoading = true;

      try {
        const result = await getUsersByRole(this.token, role.id);
        log("Users by role loaded:", result);

        if (!result.error) {
          this.usersWithRole = result.users_by_client || [];
        } else {
          this.showSnackbar(result.message || 'Error loading users', 'error');
        }
      } catch (err) {
        log("Error loading users with role:", err);
        this.showSnackbar('Error loading users', 'error');
      } finally {
        this.viewUsersLoading = false;
      }
    },

    async assignRoleFromViewUsers() {
      // Pre-fill the role and open assign dialog
      this.assignData = {
        role_id: this.viewUsersRole?.id || null,
        client_id: null,
        user_id: null
      };
      this.usersList = [];
      this.assignDialog = true;

      // Load clients list
      try {
        const result = await getClientTree(this.token);
        log("Clients loaded:", result);
        if (!result.error) {
          this.clientsList = result.clients_list || [];
        } else {
          this.showSnackbar(result.message || 'Error loading clients', 'error');
        }
      } catch (err) {
        log("Error loading clients:", err);
        this.showSnackbar('Error loading clients', 'error');
      }
    },

    // Permissions methods
    openPermissionsDialog(role) {
      this.selectedRoleForPermissions = role;
      this.permissionsDialog = true;
    },

    onPermissionsSaved() {
      this.showSnackbar('Permissions saved successfully');
      this.permissionsDialog = false;
    }
  },
  async created() {
    const { getLoggedIn } = useGetLoggedIn();
    await getLoggedIn();
    this.token = store.getters.getToken;
    this.userLoaded = true;
    await this.loadRoles();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
